import Dish, { DishId, DishForView, DishState } from './Dish';
import Category, { CategoryId, CategoryForView, CategoryState } from './Category';
import { IntlShape } from 'react-intl';

export interface MenuState {
  dishes: DishState[];
  categories: CategoryState[];
}

interface MenuIndex {
  allDishes: Map<DishId, Dish>;
  dishIndex: DishId[];
  allCategories: Map<CategoryId, Category>;
  categoryIndex: CategoryId[];
}

export class Menu {
  private index: MenuIndex;

  constructor(state: MenuState) {
    const dishList = state.dishes.map(dishState => Dish.fromState(dishState));
    const categoryList = state.categories.map(categoryState => Category.fromState(categoryState));
    this.index = {
      allDishes: new Map(dishList.map(dish => [dish.getId(), dish])),
      dishIndex: dishList.map(dish => dish.getId()),
      allCategories: new Map(categoryList.map(category => [category.getId(), category])),
      categoryIndex: categoryList.map(category => category.getId()),
    }
  }

  public static fromState(state: MenuState): Menu {
    return new Menu(state);
  }

  public getDishById(id: DishId): Dish {
    return this.index.allDishes.get(id)!;
  }

  public getCategoryById(id: CategoryId): Category {
    return this.index.allCategories.get(id)!;
  }

  private getDishList(): Dish[] {
    return this.index.dishIndex.map(dishId => this.getDishById(dishId)!);
  }

  private getCategoryList(): Category[] {
    return this.index.categoryIndex.map(categoryId => this.getCategoryById(categoryId)!);
  }

  public getDishIdsInUse(): Set<DishId> {
    return new Set<DishId>(this.getCategoryList().flatMap(c => c.getDishList()));
  }

  public getForView(intl: IntlShape): MenuForView {
    const getDishFun = (id: DishId) => this.getDishById(id).getForView(intl);
    return new MenuForView({
      dishes: this.getDishList().map(d => d.getForView(intl)),
      categories: this.getCategoryList().map(c => c.getForView(intl, getDishFun)),
    });
  }
}

interface MenuForViewProps {
  dishes: DishForView[];
  categories: CategoryForView[];
}

export class MenuForView {
  private state: MenuForViewProps;

  constructor(props: MenuForViewProps) {
    this.state = { ...props };
  }

  public getDishes(): DishForView[] {
    return this.state.dishes;
  }

  public getCategories(): CategoryForView[] {
    return this.state.categories;
  }
}

export default Menu;
